// ─────────────────────────────────────────────
//  Specialty taxonomy — surgical specialties for a facility.
//
//  Specialties are EDITABLE config stored on the facility document
//  (facility.specialties: [{ key, label, active, custom }]). When a facility
//  has no saved list yet, we fall back to PRESET_SPECIALTIES below. The same
//  resolved list feeds the surgeon "specialty" dropdown and the specialty boxes
//  surgeons are grouped into. Facility-specific (each facility keeps its own).
// ─────────────────────────────────────────────

// Preset list used until an admin customizes it. `key` is the stable value
// stored on a surgeon; `label` is what users see. Ordered alphabetically with
// "Other" pinned last (see sortSpecialties) — new custom entries slot into the
// same alphabetical order.
export const PRESET_SPECIALTIES = [
  { key: "cardiology",               label: "Cardiology",               active: true, custom: false },
  { key: "cardiothoracic",           label: "Cardiothoracic",           active: true, custom: false },
  { key: "ent",                      label: "ENT",                      active: true, custom: false },
  { key: "general_surgery",          label: "General Surgery",          active: true, custom: false },
  { key: "gi",                       label: "GI",                       active: true, custom: false },
  { key: "intensivist_pulmonologist", label: "Intensivist Pulmonologist", active: true, custom: false },
  { key: "neurosurgery",             label: "Neurosurgery",             active: true, custom: false },
  { key: "ob_gyn",                   label: "OB GYN",                   active: true, custom: false },
  { key: "orthopedics",              label: "Orthopedics",              active: true, custom: false },
  { key: "podiatry",                 label: "Podiatry",                 active: true, custom: false },
  { key: "radiology",                label: "Radiology",                active: true, custom: false },
  { key: "trauma",                   label: "Trauma",                   active: true, custom: false },
  { key: "urology",                  label: "Urology",                  active: true, custom: false },
  { key: "other",                    label: "Other",                    active: true, custom: false },
];

// Sort specialties alphabetically by label, with the catch-all "Other" pinned
// last regardless of name. Used so add/edit keep the list in order.
export function sortSpecialties(list) {
  return [...list].sort((a, b) => {
    if (a.key === "other") return 1;
    if (b.key === "other") return -1;
    return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
  });
}

// Turn a free-text label into a stable storage key: "Plastic Surgery" → "plastic_surgery".
export function slugify(label) {
  return String(label)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "specialty";
}

// Fallback label for an unknown key: "plastic_surgery" → "Plastic Surgery".
export function prettify(key) {
  return String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Use the facility's saved list if present, otherwise the presets.
export function resolveSpecialties(facilitySpecialties) {
  return Array.isArray(facilitySpecialties) && facilitySpecialties.length > 0
    ? facilitySpecialties
    : PRESET_SPECIALTIES;
}

// The label for a stored specialty key, given the resolved list.
export function labelFor(specialties, key) {
  const hit = specialties.find((sp) => sp.key === key);
  return hit ? hit.label : prettify(key);
}

// Active specialties only — what the surgeon "specialty" dropdown offers.
export function activeSpecialties(specialties) {
  return resolveSpecialties(specialties).filter((sp) => sp.active !== false);
}
